import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { Snackbar, Alert, AlertColor, Slide, SlideProps } from '@mui/material';

export interface SnackbarMessage {
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface SnackbarContextType {
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showSnackbar: (snackbar: SnackbarMessage) => void;
  hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children: ReactNode;
}

function SlideTransition(props: Readonly<SlideProps>) {
  return <Slide {...props} direction="up" />;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<SnackbarMessage | null>(null);
  const [open, setOpen] = useState(false);

  const showSnackbar = useCallback((snackbarData: SnackbarMessage) => {
    setSnackbar(snackbarData);
    setOpen(true);
  }, []);

  const showSuccess = useCallback((message: string, duration = 4000) => {
    showSnackbar({ message, severity: 'success', duration });
  }, [showSnackbar]);

  const showError = useCallback((message: string, duration = 6000) => {
    showSnackbar({ message, severity: 'error', duration });
  }, [showSnackbar]);

  const showWarning = useCallback((message: string, duration = 5000) => {
    showSnackbar({ message, severity: 'warning', duration });
  }, [showSnackbar]);

  const showInfo = useCallback((message: string, duration = 4000) => {
    showSnackbar({ message, severity: 'info', duration });
  }, [showSnackbar]);

  const hideSnackbar = useCallback(() => {
    setOpen(false);
    setTimeout(() => setSnackbar(null), 200);
  }, []);

  const handleClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    hideSnackbar();
  }, [hideSnackbar]);

  const value: SnackbarContextType = useMemo(() => ({
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showSnackbar,
    hideSnackbar,
  }), [showSuccess, showError, showWarning, showInfo, showSnackbar, hideSnackbar]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {snackbar && (
        <Snackbar
          open={open}
          autoHideDuration={snackbar.duration || 4000}
          onClose={handleClose}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              minWidth: '300px',
            },
          }}
        >
          <Alert
            onClose={handleClose}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: '100%',
              fontSize: '0.875rem',
              fontWeight: 500,
              boxShadow: 3,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </SnackbarContext.Provider>
  );
};